import React, { useState } from 'react';
import { useUploadQueue, formatBytes, formatSpeed, formatETA } from '../context/UploadQueueContext';
import { X, ChevronDown, ChevronUp, Check, AlertCircle, Clock, Pause, Play, RotateCcw, RefreshCw } from 'lucide-react';

export default function UploadQueuePanel({ onRefresh }) {
  const {
    queue, cancelItem, retryItem, retryAllFailed,
    pauseItem, resumeItem, removeFromQueue, clearCompleted,
    waitingCount, doneCount, failedCount,
  } = useUploadQueue();

  const [collapsed, setCollapsed] = useState(false);
  if (!queue.length) return null;

  const uploadingItem = queue.find(i => i.status === 'uploading');
  const totalItems = queue.length;
  const completedItems = queue.filter(i => i.status === 'done').length;
  const failedItems = queue.filter(i => i.status === 'failed');
  const overallPct = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  return (
    <>
      <style>{`
        @keyframes shimmerQ {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        .qrow:hover { background: #f5f3ff !important; }
      `}</style>
      <div style={{
        position: 'fixed', bottom: 0, right: 24, width: 460,
        background: '#ffffff',
        border: '1.5px solid #e8e4f3',
        borderBottom: 'none',
        borderRadius: '14px 14px 0 0',
        boxShadow: '0 -4px 24px rgba(124,58,237,0.1), 0 -1px 4px rgba(0,0,0,0.06)',
        zIndex: 3000,
        fontFamily: "'Poppins', sans-serif",
        maxHeight: collapsed ? 58 : 480,
        transition: 'max-height 0.3s cubic-bezier(0.22,1,0.36,1)',
        overflow: 'hidden',
      }}>
        {/* Progress line at very top */}
        {!collapsed && queue.filter(i => ['uploading','waiting'].includes(i.status)).length > 0 && (
          <div style={{ height: 3, background: '#ede9fe', borderRadius: '14px 14px 0 0', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${overallPct}%`, background: 'linear-gradient(90deg, #7c3aed, #8b5cf6, #a78bfa)', backgroundSize: '200% auto', animation: 'shimmerQ 2s linear infinite', transition: 'width 0.5s ease' }}/>
          </div>
        )}

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '0 16px', height: 54, gap: 10, borderBottom: collapsed ? 'none' : '1px solid #f0edfb' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, flex:1, cursor:'pointer' }} onClick={() => setCollapsed(!collapsed)}>
            {uploadingItem ? (
              <div style={{ width:18, height:18, border:'2.5px solid #ede9fe', borderTop:'2.5px solid #7c3aed', borderRadius:'50%', animation:'spin 0.8s linear infinite', flexShrink:0 }}/>
            ) : failedItems.length > 0 ? (
              <AlertCircle size={16} color="#dc2626"/>
            ) : (
              <div style={{ width:18, height:18, borderRadius:'50%', background:'#d1fae5', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <Check size={10} color="#059669"/>
              </div>
            )}
            <span style={{ fontWeight:600, fontSize:13, color:'#1a1035' }}>
              {uploadingItem ? `Uploading ${completedItems+1} of ${totalItems}` : failedItems.length>0 ? `${failedItems.length} failed` : `${completedItems} complete`}
            </span>
            {waitingCount > 0 && <span style={{ background:'#ede9fe', color:'#6d28d9', fontSize:10, fontWeight:600, padding:'1px 8px', borderRadius:20 }}>{waitingCount} queued</span>}
            {failedItems.length > 0 && <span style={{ background:'#fee2e2', color:'#dc2626', fontSize:10, fontWeight:600, padding:'1px 8px', borderRadius:20 }}>{failedItems.length} failed</span>}
          </div>

          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <button onClick={e => { e.stopPropagation(); onRefresh?.(); }}
              style={{ display:'flex', alignItems:'center', gap:4, background:'#f5f3ff', border:'1px solid #e9d5ff', color:'#6d28d9', fontSize:11, fontWeight:600, cursor:'pointer', padding:'4px 10px', borderRadius:7, fontFamily:"'Poppins',sans-serif" }}>
              <RefreshCw size={11}/> Refresh
            </button>
            {failedItems.length > 0 && (
              <button onClick={e => { e.stopPropagation(); retryAllFailed(); }}
                style={{ display:'flex', alignItems:'center', gap:4, background:'#fef3c7', border:'1px solid #fcd34d', color:'#92400e', fontSize:11, fontWeight:600, cursor:'pointer', padding:'4px 10px', borderRadius:7, fontFamily:"'Poppins',sans-serif" }}>
                <RotateCcw size={11}/> Retry All
              </button>
            )}
            {doneCount > 0 && (
              <button onClick={e => { e.stopPropagation(); clearCompleted(); }}
                style={{ background:'transparent', border:'none', color:'#b8b3cc', fontSize:11, cursor:'pointer', fontFamily:"'Poppins',sans-serif" }}>
                Clear
              </button>
            )}
            <button onClick={() => setCollapsed(!collapsed)}
              style={{ background:'transparent', border:'none', color:'#b8b3cc', cursor:'pointer', display:'flex', padding:2 }}>
              {collapsed ? <ChevronUp size={15}/> : <ChevronDown size={15}/>}
            </button>
          </div>
        </div>

        {/* List */}
        {!collapsed && (
          <div style={{ overflow:'auto', maxHeight:420 }}>
            {/* Failed section */}
            {failedItems.length > 0 && (
              <div style={{ background:'#fff7f7', borderBottom:'1px solid #fecaca' }}>
                <div style={{ fontWeight:600, fontSize:11, color:'#dc2626', letterSpacing:1, textTransform:'uppercase', padding:'8px 16px 4px' }}>Failed — Retry to re-upload</div>
                {failedItems.map(item => (
                  <div key={item.id} className="qrow" style={{ display:'flex', alignItems:'flex-start', gap:10, padding:'10px 16px', borderBottom:'1px solid #fee2e2' }}>
                    <AlertCircle size={13} color="#dc2626" style={{ flexShrink:0, marginTop:2 }}/>
                    <div style={{ flex:1, overflow:'hidden' }}>
                      <div style={{ fontWeight:600, fontSize:13, color:'#1a1035', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', marginBottom:2 }}>{item.name}</div>
                      <div style={{ fontWeight:400, fontSize:11, color:'#dc2626' }}>
                        Network error or timeout
                        {item.retryCount > 0 && <span style={{ color:'#b8b3cc', marginLeft:8 }}>Retried {item.retryCount}×</span>}
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:5 }}>
                      <button onClick={() => retryItem(item.id)}
                        style={{ display:'flex', alignItems:'center', gap:4, background:'#fef3c7', border:'1px solid #fcd34d', color:'#92400e', cursor:'pointer', borderRadius:7, padding:'4px 10px', fontSize:11, fontWeight:600, fontFamily:"'Poppins',sans-serif" }}>
                        <RotateCcw size={10}/> Retry
                      </button>
                      <button onClick={() => removeFromQueue(item.id)}
                        style={{ background:'#f5f3ff', border:'1px solid #e9d5ff', color:'#8b7fb0', cursor:'pointer', borderRadius:7, width:26, height:26, display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <X size={11}/>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Active items */}
            {queue.filter(i => i.status !== 'failed').map(item => (
              <div key={item.id} className="qrow" style={{ display:'flex', alignItems:'flex-start', gap:10, padding:'10px 16px', borderBottom:'1px solid #f0edfb' }}>
                <div style={{ flexShrink:0, marginTop:3 }}>
                  {item.status==='uploading' && <div style={{ width:13, height:13, border:'2px solid #ede9fe', borderTop:'2px solid #7c3aed', borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/>}
                  {item.status==='waiting' && <Clock size={13} color="#b8b3cc"/>}
                  {item.status==='paused' && <Pause size={13} color="#d97706"/>}
                  {item.status==='done' && <div style={{ width:13, height:13, borderRadius:'50%', background:'#d1fae5', display:'flex', alignItems:'center', justifyContent:'center' }}><Check size={8} color="#059669"/></div>}
                  {item.status==='cancelled' && <X size={13} color="#b8b3cc"/>}
                </div>

                <div style={{ flex:1, overflow:'hidden', minWidth:0 }}>
                  <div style={{ fontWeight:600, fontSize:13, color:'#1a1035', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', marginBottom:3 }}>{item.name}</div>
                  {item.status==='uploading' && (
                    <div style={{ display:'flex', gap:10, marginBottom:6, flexWrap:'wrap' }}>
                      <span style={{ fontWeight:400, fontSize:11, color:'#8b7fb0' }}>{formatBytes(item.loaded)} / {formatBytes(item.total)}</span>
                      {item.speed > 0 && <span style={{ fontWeight:700, fontSize:11, color:'#7c3aed' }}>{formatSpeed(item.speed)}</span>}
                      {item.eta > 0 && <span style={{ fontWeight:500, fontSize:11, color:'#d97706' }}>{formatETA(item.eta)}</span>}
                    </div>
                  )}
                  {item.status==='waiting' && <div style={{ fontWeight:400, fontSize:11, color:'#b8b3cc', marginBottom:5 }}>{formatBytes(item.total)} · In queue</div>}
                  {item.status==='paused' && <div style={{ fontWeight:500, fontSize:11, color:'#d97706', marginBottom:5 }}>Paused at {item.progress}%</div>}
                  {item.status==='done' && <div style={{ fontWeight:400, fontSize:11, color:'#059669', marginBottom:2 }}>Uploaded · {formatBytes(item.total)}</div>}

                  {['uploading','waiting','paused'].includes(item.status) && (
                    <div style={{ height:4, background:'#ede9fe', borderRadius:10, overflow:'hidden' }}>
                      <div style={{ height:'100%', width:`${item.progress}%`, background: item.status==='paused' ? '#f59e0b' : item.status==='waiting' ? '#c4b5fd' : 'linear-gradient(90deg, #7c3aed, #8b5cf6)', borderRadius:10, transition:'width 0.3s ease' }}/>
                    </div>
                  )}
                </div>

                <div style={{ display:'flex', alignItems:'center', gap:4, flexShrink:0 }}>
                  {item.status==='uploading' && (
                    <>
                      <span style={{ fontWeight:700, fontSize:11, color:'#7c3aed', width:32, textAlign:'right' }}>{item.progress}%</span>
                      <button onClick={() => pauseItem(item.id)} style={{ background:'#fef3c7', border:'1px solid #fcd34d', color:'#92400e', cursor:'pointer', borderRadius:7, width:24, height:24, display:'flex', alignItems:'center', justifyContent:'center' }}><Pause size={10}/></button>
                      <button onClick={() => cancelItem(item.id)} style={{ background:'#fee2e2', border:'1px solid #fca5a5', color:'#dc2626', cursor:'pointer', borderRadius:7, width:24, height:24, display:'flex', alignItems:'center', justifyContent:'center' }}><X size={10}/></button>
                    </>
                  )}
                  {item.status==='paused' && (
                    <>
                      <button onClick={() => resumeItem(item.id)} style={{ background:'#d1fae5', border:'1px solid #6ee7b7', color:'#059669', cursor:'pointer', borderRadius:7, width:24, height:24, display:'flex', alignItems:'center', justifyContent:'center' }}><Play size={10} fill="#059669"/></button>
                      <button onClick={() => cancelItem(item.id)} style={{ background:'#fee2e2', border:'1px solid #fca5a5', color:'#dc2626', cursor:'pointer', borderRadius:7, width:24, height:24, display:'flex', alignItems:'center', justifyContent:'center' }}><X size={10}/></button>
                    </>
                  )}
                  {item.status==='waiting' && (
                    <button onClick={() => cancelItem(item.id)} style={{ background:'#f5f3ff', border:'1px solid #e9d5ff', color:'#8b7fb0', cursor:'pointer', borderRadius:7, width:24, height:24, display:'flex', alignItems:'center', justifyContent:'center' }}><X size={10}/></button>
                  )}
                  {['done','cancelled'].includes(item.status) && (
                    <button onClick={() => removeFromQueue(item.id)} style={{ background:'#f5f3ff', border:'1px solid #e9d5ff', color:'#b8b3cc', cursor:'pointer', borderRadius:7, width:24, height:24, display:'flex', alignItems:'center', justifyContent:'center' }}><X size={10}/></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}